import {
    type ChangeEvent,
    type FocusEvent,
    type ForwardRefExoticComponent,
    type ForwardedRef,
    type HTMLAttributes,
    type KeyboardEvent,
    type PropsWithoutRef,
    type ReactElement,
    type RefAttributes,
    type RefObject,
    forwardRef,
    useCallback,
    useRef
} from "react"

import isEmpty from "lodash-es/isEmpty"
import isFunction from "lodash-es/isFunction"
import { useCss, useIsomorphicLayoutEffect } from "react-use"

import { THEME } from "@/3514/theme"
import { buildCssClassName, isBrowserVersionGreater, isFirefoxBrowser } from "@/3514/utils"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import {
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    Typography,
    typographyColorsClassMap,
    typographyLineHeightsMap,
    typographySizesClassMap
} from "../../typography"

import { textareaConfig } from "./textarea.config"
import {
    ETextAreaScrollbarVariant as EScrollbarVariant,
    ETextAreaBorderWidth,
    ETextAreaVariant
} from "./textarea.types"

type TProps = {
    variant?: ETextAreaVariant
    name?: string
    defaultValue?: string
    value?: string
    textColor?: ETypographyColor
    textSize?: ETypographySize
    placeholderTextColor?: ETypographyColor
    placeholderTextSize?: ETypographySize
    placeholder?: string
    error?: string
    scrollbarVariant?: EScrollbarVariant
    onClick?: TEmptyCallback
    onChange?(event?: ChangeEvent<HTMLTextAreaElement>): void
    onKeyDown?(event?: KeyboardEvent): void
    onFocus?: TEmptyCallback
    onBlur?: TEmptyCallback | ((event: FocusEvent) => void)
    className?: string
    isDisabled?: boolean
    isRequired?: boolean
    isAreaRequired?: boolean
    borderWidth?: ETextAreaBorderWidth
    borderRadius?: typeof textareaConfig.DEFAULT_BORDER_RADIUS
    minLines?: number
    maxLines?: number
    maxSymbols?: number
    withDynamicHeight?: boolean
    withNoBorder?: boolean
    withItalicPlaceholder?: boolean
    withNoResize?: boolean
    withNoPaddings?: boolean
    withSpellingCssOverride?: boolean
    enterKeyHint?: Pick<HTMLAttributes<HTMLTextAreaElement>, "enterKeyHint">["enterKeyHint"]
    ariaLabelledBy?: string
    ariaDescribedBy?: string
}

function _getTextareaLineHeightBySize(textSize: ETypographySize): number {
    return typographyLineHeightsMap[textSize]
}

const isSpellingClassSupported: boolean =
    !isFirefoxBrowser() &&
    isBrowserVersionGreater("edge", "121") &&
    isBrowserVersionGreater("chrome", "121") &&
    isBrowserVersionGreater("safari", "17.4")

const Component: ForwardRefExoticComponent<PropsWithoutRef<TProps> & RefAttributes<HTMLTextAreaElement>> = forwardRef<
    HTMLTextAreaElement,
    TProps
>(
    (
        {
            variant = ETextAreaVariant.Default,
            name = String(),
            defaultValue,
            value,
            textColor = ETypographyColor.Dark,
            textSize = ETypographySize.Medium,
            placeholderTextColor = ETypographyColor.Gray23,
            placeholderTextSize = ETypographySize.Medium,
            placeholder = String(),
            error = String(),
            scrollbarVariant = EScrollbarVariant.Transparent,
            onClick = emptyCallback,
            onChange,
            onKeyDown,
            onFocus,
            onBlur,
            className = String(),
            isDisabled = false,
            isRequired = false,
            isAreaRequired = false,
            borderWidth = ETextAreaBorderWidth.Medium,
            borderRadius = textareaConfig.DEFAULT_BORDER_RADIUS,
            minLines = textareaConfig.DEFAULT_MIN_LINES,
            maxLines = textareaConfig.DEFAULT_MAX_LINES,
            maxSymbols,
            withDynamicHeight = false,
            withNoBorder = false,
            withItalicPlaceholder = false,
            withNoResize = false,
            withNoPaddings = false,
            withSpellingCssOverride = false,
            enterKeyHint,
            ariaLabelledBy = undefined,
            ariaDescribedBy = undefined,
            ...rest
        }: TProps,
        ref: ForwardedRef<HTMLTextAreaElement>
    ): ReactElement => {
        const textareaRef: RefObject<HTMLTextAreaElement> = useRef<HTMLTextAreaElement>(null)

        const updateHeight: TEmptyCallback = useCallback((): void => {
            if (textareaRef.current && withDynamicHeight) {
                const textArea: HTMLTextAreaElement = textareaRef.current
                textArea.style.height = "auto"
                textArea.style.height = `${Math.min(textArea.scrollHeight, maxLines * _getTextareaLineHeightBySize(textSize))}px`
                textArea.style.overflowY =
                    textArea.scrollHeight > maxLines * _getTextareaLineHeightBySize(textSize) ? "auto" : "hidden"
            }
        }, [withDynamicHeight, maxLines, textSize])

        function handleChange(e: ChangeEvent<HTMLTextAreaElement>): void {
            if (isFunction(onChange)) onChange(e)
            updateHeight()
        }

        useIsomorphicLayoutEffect((): TEmptyCallback => {
            if (!textareaRef.current || !withDynamicHeight) return emptyCallback

            const textArea: HTMLTextAreaElement = textareaRef.current

            textArea.addEventListener("input", updateHeight)

            return (): void => void textArea.removeEventListener("input", updateHeight)
        }, [withDynamicHeight, maxLines, textSize, updateHeight])

        const insertedClasses: string = useCss({
            ...(Boolean(withSpellingCssOverride && isSpellingClassSupported) && {
                "&::spelling-error": {
                    textDecoration: "none",
                    backgroundColor: THEME.COLORS.red[300]
                }
            })
        })

        return (
            <div className={textareaConfig.classes.container}>
                <label htmlFor={name} className={textareaConfig.classes.label}>
                    {name}
                </label>
                <textarea
                    {...rest}
                    enterKeyHint={enterKeyHint}
                    ref={(node: HTMLTextAreaElement): void => {
                        if (isFunction(ref)) ref(node)
                        else if (ref) ref.current = node
                        textareaRef.current = node
                    }}
                    style={{
                        borderRadius: `${borderRadius.tl}px ${borderRadius.tr}px ${borderRadius.bl}px ${borderRadius.br}px`
                    }}
                    className={buildCssClassName(
                        !withDynamicHeight && `min-h-[${minLines * _getTextareaLineHeightBySize(textSize)}px]`,
                        `placeholder:${typographySizesClassMap[placeholderTextSize]}`,
                        `placeholder:${typographyColorsClassMap[placeholderTextColor]}`,
                        {
                            "!border-[0px]": withNoBorder,
                            "placeholder:italic": withItalicPlaceholder,
                            "resize-none": withNoResize,
                            "!p-[0px]": withNoPaddings,
                            "cursor-not-allowed": isDisabled,
                            "[field-sizing:content]": withDynamicHeight,
                            "bg-white": isDisabled,
                            [typographySizesClassMap[textSize]]: textSize,
                            [typographyColorsClassMap[textColor]]: textColor,
                            [textareaConfig.variantClassesMap[variant]()]: variant,
                            [textareaConfig.borderWidthMap[borderWidth]]: borderWidth,
                            [textareaConfig.scrollbarsClassesMap[scrollbarVariant](variant)]: scrollbarVariant,
                            [insertedClasses]: insertedClasses,
                            [className]: className
                        }
                    )}
                    aria-disabled={isDisabled}
                    disabled={isDisabled}
                    aria-required={isAreaRequired}
                    aria-labelledby={ariaLabelledBy || name}
                    aria-describedby={ariaDescribedBy}
                    required={isRequired}
                    tabIndex={0}
                    name={name}
                    id={name}
                    defaultValue={defaultValue}
                    maxLength={maxSymbols}
                    value={value}
                    aria-placeholder={placeholder}
                    placeholder={placeholder}
                    rows={minLines}
                    onKeyDown={onKeyDown}
                    onChange={handleChange}
                    onClick={onClick}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
                {!isEmpty(error) && (
                    <Typography
                        text={error}
                        className={textareaConfig.classes.error}
                        tag={ETypographyTag.Span}
                        size={ETypographySize.ExtraSmall}
                        color={ETypographyColor.Error}
                    />
                )}
            </div>
        )
    }
)

Component.displayName = "Textarea"

export { Component as TextArea }
